import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '@/components/ui/popover';
import { Bell, WarningCircle, Clock, ListChecks, Receipt, Checks } from '@phosphor-icons/react';

const iconFor = (type) => {
  if (type.includes('invoice')) return Receipt;
  if (type.includes('task')) return ListChecks;
  return Bell;
};

export default function NotificationsBell() {
  const [data, setData] = useState({ notifications: [], unread_count: 0 });
  const [open, setOpen] = useState(false);
  const nav = useNavigate();

  const load = () => api.get('/notifications').then(({ data }) => setData(data)).catch(() => {});
  useEffect(() => {
    load();
    const t = setInterval(load, 30000); // refresh every 30s
    return () => clearInterval(t);
  }, []);

  const markRead = async (id) => {
    await api.post('/notifications/read', { id });
    load();
  };

  const markAllRead = async () => {
    await api.post('/notifications/read-all');
    load();
  };

  const click = (n) => {
    markRead(n.id);
    if (n.entity_type === 'invoice') nav(`/invoices/${n.entity_id}`);
    else if (n.entity_type === 'task') nav('/tasks');
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" data-testid="notifications-button">
          <Bell size={20} className="text-slate-600" />
          {data.unread_count > 0 && (
            <span className="absolute top-1 left-1 h-5 min-w-[20px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center" data-testid="notifications-badge">
              {data.unread_count}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-96 p-0" data-testid="notifications-panel">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
          <div className="font-semibold text-slate-900">الإشعارات</div>
          {data.unread_count > 0 && (
            <Button size="sm" variant="ghost" onClick={markAllRead} className="text-xs" data-testid="mark-all-read">
              <Checks className="ms-1" size={14} /> قراءة الكل
            </Button>
          )}
        </div>
        <div className="max-h-[500px] overflow-y-auto">
          {data.notifications.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-500">
              لا توجد إشعارات
            </div>
          ) : (
            data.notifications.map((n) => {
              const Icon = iconFor(n.type);
              const sev = n.severity === 'high' ? 'text-red-600 bg-red-50' : 'text-amber-600 bg-amber-50';
              return (
                <button
                  key={n.id}
                  onClick={() => click(n)}
                  className={`w-full text-start px-4 py-3 flex items-start gap-3 hover:bg-slate-50 border-b border-slate-100 transition-colors ${!n.read ? 'bg-blue-50/30' : ''}`}
                  data-testid={`notification-${n.type}`}
                >
                  <div className={`w-8 h-8 rounded-md flex items-center justify-center shrink-0 ${sev}`}>
                    <Icon size={16} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-900 flex items-center gap-2">
                      {!n.read && <span className="w-1.5 h-1.5 rounded-full bg-blue-600" />}
                      {n.title}
                    </div>
                    <div className="text-xs text-slate-600 mt-0.5">{n.message}</div>
                  </div>
                </button>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
